import { EventType, MoveEvent } from "@fatbees/schemas";
import { useQuantizedNow } from "@fatbees/ui-utils";
import { eq, lte } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { groupBy, prop } from "ramda";
import { useEffect } from "react";
import { useCollectionReady } from "./useCollectionReady";
import { EventsCollection } from "./useEventsCollectionInstance";
import { HivesCollection } from "./useHivesCollectionInstance";

export function useUpdateHiveLocations(
  eventsCollection: EventsCollection,
  hivesCollection: HivesCollection,
) {
  const quantizedNow = useQuantizedNow();
  const ready = useCollectionReady(hivesCollection);

  const moveEvents = useLiveQuery(
    (q) =>
      q
        .from({ event: eventsCollection })
        .where(({ event }) => eq(event.type, EventType.move))
        .where(({ event }) => lte(event.start, quantizedNow))
        .orderBy(({ event }) => event.start),
    [quantizedNow],
  ).data as (MoveEvent & { loadId: string })[];

  useEffect(() => {
    if (!ready) {
      return;
    }

    const eventsByLoadId = groupBy(prop("loadId"), moveEvents);

    hivesCollection.utils.writeBatch(() => {
      for (const hive of hivesCollection.values()) {
        // values() includes optimistic rows, but writeUpdate validates
        // against the synced store only — updating a hive whose insert is
        // still in flight throws UpdateOperationItemNotFoundError and
        // crashes the tree. Skip it; its location is recomputed on a later
        // effect run (at the latest, useQuantizedNow's next minute tick)
        // once it has synced.
        if (!hivesCollection._state.syncedData.has(hive.id)) {
          continue;
        }

        let mostRecentEvent: MoveEvent | undefined;

        for (const event of eventsByLoadId[hive.loadId] ?? []) {
          if (event.hiveId === null || event.hiveId === hive.id) {
            mostRecentEvent = event;
          }
        }

        const location = mostRecentEvent && {
          since: mostRecentEvent.start,
          name: mostRecentEvent.details.movedTo,
        };

        if (JSON.stringify(location) !== JSON.stringify(hive.location)) {
          hivesCollection.utils.writeUpdate({ id: hive.id, location });
        }
      }
    });
  }, [moveEvents, ready, hivesCollection]);
}
